import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _get from 'lodash/get';
import MLIcon from '../MLIcon/MLIcon';
import s from './CustomMuiDropdown.module.css';

function CustomMuiDropdown(props) {
  const {
    title,
    items,
    onClick,
    ariaLabel,
    onClickItem,
    onClose,
    valueProp,
    labelProp,
    defaultItem,
    defaultIndex,
    defaultValue,
    isMenu,
    disabled,
    className,
    optionsClassName,
    labelClassName,
    selectedValue,
    placeholder,
    dataTestId,
    buttonLeftIcon,
    buttonRightIcon,
    hideRightIcon,
    menuAlign,
  } = props;

  let initialItem = defaultIndex ? items[defaultIndex] : defaultItem;
  if (defaultValue)
    initialItem = items.find((item) => _get(item, valueProp) === defaultValue);
  const [selectedItem, setSelectedItem] = useState(initialItem);
  const selectedItemValue = _get(
    selectedItem,
    valueProp,
    initialItem ? initialItem.value : null,
  );
  const selectedItemLabel = _get(
    selectedItem,
    labelProp,
    initialItem ? initialItem.label : placeholder,
  );

  useEffect(() => {
    const selected = items.find(
      (item) => _get(item, valueProp) === selectedValue,
    );
    setSelectedItem(selected);
  }, [selectedValue, disabled, items, valueProp]);

  const text = isMenu ? title : selectedItemLabel;
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  const filtered = items.filter((x) => !x.hide);

  const closeMenu = useCallback(
    (event) => {
      setOpen(false);
      setFocusedIndex(-1);
      if (onClose) onClose(event);
    },
    [onClose],
  );

  const handleButtonClick = (event) => {
    if (onClick) onClick(event);
    if (open) {
      closeMenu(event);
    } else {
      setOpen(true);
      setFocusedIndex(0);
    }
  };

  // Focus the item when focusedIndex changes
  useEffect(() => {
    if (open && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].focus();
    }
  }, [open, focusedIndex]);

  // Handle click outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        closeMenu(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeMenu]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(filtered.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          event.stopPropagation();
          if (focusedIndex >= 0 && filtered[focusedIndex]) {
            const item = filtered[focusedIndex];
            if (item.disabled) return;
            const btn = buttonRef.current;
            setSelectedItem(item);
            setOpen(false);
            setFocusedIndex(-1);
            if (item.returnFocus !== false) {
              setTimeout(() => btn?.focus(), 0);
            }
            if (onClickItem) onClickItem(item, event);
            if (onClose) onClose(event);
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeMenu(event);
          buttonRef.current?.focus();
          break;
        case 'Tab':
          // Allow default Tab behavior - this will close the menu
          // and move focus to the next/previous element naturally
          closeMenu(event);
          break;
        default:
          break;
      }
    },
    [open, filtered, focusedIndex, closeMenu, onClickItem, onClose],
  );

  return (
    <div className={s.dropdownContainer}>
      <button
        ref={buttonRef}
        type="button"
        className={classNames(s.button, s.btnSecondary, className)}
        aria-label={ariaLabel || text}
        onClick={handleButtonClick}
        data-test-id={dataTestId || 'menu_dropdown_button'}
        disabled={disabled}
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls="custom-dropdown-menu"
      >
        {buttonLeftIcon}
        <span className={classNames(s.buttonLabel, labelClassName)}>
          {text}
        </span>
        {!hideRightIcon &&
          (buttonRightIcon || (
            <MLIcon className={s.menuBtnIcon} type="caret_down" />
          ))}
      </button>

      {open && (
        <ul
          ref={menuRef}
          id="custom-dropdown-menu"
          role="listbox"
          className={classNames(s.menu, optionsClassName, {
            [s.menuAlignRight]: menuAlign === 'right',
          })}
          onKeyDown={handleKeyDown}
          data-test-id={dataTestId || 'menu-dropdown-container'}
        >
          {filtered.map((item, index) => {
            const itemLabel = _get(item, labelProp, '');
            const itemValue = _get(item, valueProp, '');
            const itemIcon = item.icon;
            const isSelected = !isMenu && itemValue === selectedItemValue;
            const isItemSelected = item.selected;
            const isDisabled = item.disabled;

            return (
              <li
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                role="option"
                aria-selected={isSelected || isItemSelected}
                aria-disabled={isDisabled}
                tabIndex={focusedIndex === index ? 0 : -1}
                className={classNames(s.menuItem, {
                  [s.menuItemDisabled]: isDisabled,
                })}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isDisabled) return;
                  const btn = buttonRef.current;
                  setSelectedItem(item);
                  setOpen(false);
                  setFocusedIndex(-1);
                  if (item.returnFocus !== false) {
                    setTimeout(() => btn?.focus(), 0);
                  }
                  if (onClickItem) onClickItem(item, event);
                  if (onClose) onClose(event);
                }}
                data-test-id={`menu_item_${itemValue}`}
              >
                {itemIcon &&
                  (typeof itemIcon === 'string' ? (
                    <MLIcon type={itemIcon} className={s.optionLeftIcon} />
                  ) : (
                    itemIcon
                  ))}
                <span className={s.optionLabel}>{itemLabel}</span>
                {(isSelected || isItemSelected) && (
                  <MLIcon type="check" className={s.menuItemIcon} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

CustomMuiDropdown.defaultProps = {
  labelProp: 'label',
  valueProp: 'value',
  defaultIndex: null,
  defaultValue: null,
  defaultItem: null,
  isMenu: false,
  items: [],
  selectedValue: null,
  placeholder: '',
  optionsClassName: '',
  labelClassName: '',
  buttonLeftIcon: null,
  buttonRightIcon: null,
  hideRightIcon: false,
  menuAlign: 'left',
};

CustomMuiDropdown.propTypes = {
  valueProp: PropTypes.string,
  labelProp: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  onClickItem: PropTypes.func,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  defaultItem: PropTypes.object,
  defaultIndex: PropTypes.number,
  defaultValue: PropTypes.any,
  isMenu: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  optionsClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  selectedValue: PropTypes.any,
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
  buttonLeftIcon: PropTypes.node,
  buttonRightIcon: PropTypes.node,
  hideRightIcon: PropTypes.bool,
  menuAlign: PropTypes.oneOf(['left', 'right']),
};

export default CustomMuiDropdown;
